import React, { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api, { fileUrl } from '../lib/api';
import Logo from './Logo';
import Footer from './Footer';
import LiveNow from './LiveNow';
import { Home, Compass, ListMusic, Bookmark, MessageSquare, User, Upload, LogOut, Search, Menu, X, Disc3, Radio, Sparkles, Wand2, Scissors } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Grouped so the DJ tools always sit at the top of the drawer where they belong.
// Bumping the group order also busts stale-bundle caches so users see the update.
const NAV_GROUPS = [
  {
    heading: null,
    items: [
      { to: '/dashboard', label: 'Feed', icon: Home, testid: 'nav-feed' },
      { to: '/discover', label: 'Discover', icon: Compass, testid: 'nav-discover' },
    ],
  },
  {
    heading: 'DJ Tools',
    items: [
      { to: '/decks', label: 'Live Decks', icon: Disc3, testid: 'nav-decks' },
      { to: '/loop-station', label: 'Loop Station', icon: Radio, testid: 'nav-loop-station' },
      { to: '/beat-lab', label: 'Beat Lab', icon: Sparkles, testid: 'nav-beat-lab' },
      { to: '/ai-studio', label: 'AI Studio', icon: Wand2, testid: 'nav-ai-studio' },
    ],
  },
  {
    heading: 'Live & Social',
    items: [
      { to: '/live', label: 'Live Battles', icon: Radio, testid: 'nav-live' },
      { to: '/chalks', label: 'Chalks', icon: Scissors, testid: 'nav-chalks' },
      { to: '/upload', label: 'Upload Mix', icon: Upload, testid: 'nav-upload' },
      { to: '/playlists', label: 'Playlists', icon: ListMusic, testid: 'nav-playlists' },
      { to: '/saved', label: 'Saved Music', icon: Bookmark, testid: 'nav-saved' },
      { to: '/messages', label: 'Messages', icon: MessageSquare, testid: 'nav-messages' },
      { to: '__profile__', label: 'My Profile', icon: User, testid: 'nav-profile-link' },
    ],
  },
];

const avatarFallback = (name) =>
  `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'U')}&background=FFB000&color=000&bold=true`;

export const Layout = ({ children, rightSidebar = null }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (searchQ.length < 2) {
      setSearchResults([]);
      return;
    }
    const t = setTimeout(() => {
      api.get(`/users/search?q=${encodeURIComponent(searchQ)}`)
        .then(r => setSearchResults(r.data))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [searchQ]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const sidebarContent = (
    <div className="flex flex-col h-full">
      <Link to="/dashboard" className="mb-10 inline-block">
        <Logo size="md" />
      </Link>

      <nav className="flex-1 space-y-4" data-testid="sidebar-nav">
        {NAV_GROUPS.map((group, gi) => (
          <div key={group.heading || `group-${gi}`} className="space-y-1">
            {group.heading && (
              <div className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-[0.25em] font-black text-[#FFB000]/60">
                {group.heading}
              </div>
            )}
            {group.items.map(item => {
              const Icon = item.icon;
              const to = item.to === '__profile__' ? (user ? `/profile/${user.user_id}` : '/dashboard') : item.to;
              return (
                <NavLink
                  key={item.to}
                  to={to}
                  data-testid={item.testid}
                  end={item.to === '__profile__'}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-heading font-medium ${
                      isActive
                        ? 'bg-[#FFB000]/10 text-[#FFB000] border border-[#FFB000]/20'
                        : 'text-[#A1A1AA] hover:text-white hover:bg-white/5'
                    }`
                  }
                >
                  <Icon size={18} />
                  <span className="text-sm tracking-wide">{item.label}</span>
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      {user && (
        <div className="mt-6 pt-6 border-t border-white/10 space-y-2">
          <Link
            to={`/profile/${user.user_id}`}
            className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-white/5 transition-colors"
            data-testid="nav-profile"
          >
            <img
              src={user.avatar_path ? fileUrl(user.avatar_path) : avatarFallback(user.name)}
              alt=""
              className="w-10 h-10 rounded-full object-cover border border-white/10"
            />
            <div className="min-w-0 flex-1">
              <div className="text-white text-sm font-bold truncate font-heading">{user.name}</div>
              <div className="text-[#A1A1AA] text-xs truncate">@{user.username}</div>
            </div>
          </Link>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-[#A1A1AA] hover:text-[#EF4444] hover:bg-[#EF4444]/10 transition-colors text-sm font-heading"
            data-testid="nav-logout"
          >
            <LogOut size={16} />
            Log out
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050505] text-white pb-28">
      {/* Mobile header */}
      <header className="glass-top sticky top-0 z-40 lg:hidden px-4 py-3 flex items-center justify-between">
        <Link to="/dashboard"><Logo size="sm" /></Link>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowSearch(!showSearch)}
            className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/5"
            data-testid="mobile-search-btn"
          >
            <Search size={18} />
          </button>
          <button
            onClick={() => setMobileOpen(true)}
            className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/5"
            data-testid="mobile-menu-btn"
          >
            <Menu size={20} />
          </button>
        </div>
      </header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/70 z-50 lg:hidden"
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              initial={{ x: '-100%' }} animate={{ x: 0 }} exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 30 }}
              className="fixed top-0 left-0 bottom-0 w-[280px] bg-[#0F0F11] z-[60] p-6 lg:hidden overflow-y-auto"
            >
              <button onClick={() => setMobileOpen(false)} className="absolute top-4 right-4 text-[#A1A1AA]">
                <X size={20} />
              </button>
              {sidebarContent}
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Desktop Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 max-w-[1600px] mx-auto">
        {/* Left sidebar */}
        <aside className="col-span-3 hidden lg:flex flex-col sticky top-0 h-screen p-6 border-r border-white/5">
          {sidebarContent}
        </aside>

        {/* Main */}
        <main className="col-span-1 lg:col-span-6 pt-6 px-4 md:px-8">
          {children}
          <Footer />
        </main>

        {/* Right sidebar */}
        <aside className="col-span-3 hidden xl:block sticky top-0 h-screen overflow-y-auto p-6 border-l border-white/5">
          {/* Search */}
          <div className="relative mb-6">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
            <input
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
              placeholder="Search DJs, mixes..."
              className="input-default pl-10"
              data-testid="sidebar-search"
            />
            {searchResults.length > 0 && (
              <div className="absolute top-full mt-2 left-0 right-0 bg-[#1A1A1D] border border-white/10 rounded-xl overflow-hidden z-20 max-h-80 overflow-y-auto">
                {searchResults.map(u => (
                  <Link
                    key={u.user_id}
                    to={`/profile/${u.user_id}`}
                    onClick={() => { setSearchQ(''); setSearchResults([]); }}
                    className="flex items-center gap-3 p-3 hover:bg-white/5 transition-colors"
                    data-testid={`search-result-${u.username}`}
                  >
                    <img
                      src={u.avatar_path ? fileUrl(u.avatar_path) : avatarFallback(u.name)}
                      alt=""
                      className="w-9 h-9 rounded-full object-cover"
                    />
                    <div>
                      <div className="text-white text-sm font-bold font-heading">{u.name}</div>
                      <div className="text-[#A1A1AA] text-xs">@{u.username}</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {rightSidebar}
          <LiveNow />
        </aside>
      </div>

      {/* Mobile search overlay */}
      <AnimatePresence>
        {showSearch && (
          <motion.div
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            className="fixed top-[60px] left-0 right-0 z-40 px-4 pb-4 bg-[#050505] border-b border-white/10 lg:hidden"
          >
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
              <input
                value={searchQ}
                onChange={e => setSearchQ(e.target.value)}
                placeholder="Search DJs..."
                className="input-default pl-10"
                autoFocus
              />
            </div>
            {searchResults.length > 0 && (
              <div className="mt-2 bg-[#1A1A1D] border border-white/10 rounded-xl overflow-hidden max-h-80 overflow-y-auto">
                {searchResults.map(u => (
                  <Link
                    key={u.user_id}
                    to={`/profile/${u.user_id}`}
                    onClick={() => { setSearchQ(''); setSearchResults([]); setShowSearch(false); }}
                    className="flex items-center gap-3 p-3 hover:bg-white/5 transition-colors"
                  >
                    <img
                      src={u.avatar_path ? fileUrl(u.avatar_path) : avatarFallback(u.name)}
                      alt=""
                      className="w-9 h-9 rounded-full object-cover"
                    />
                    <div>
                      <div className="text-white text-sm font-bold">{u.name}</div>
                      <div className="text-[#A1A1AA] text-xs">@{u.username}</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Layout;
